"use client";

import { useEffect, useState } from "react";
import { CheckCircle, PartyPopper, Sparkles } from "lucide-react";

interface CompletionCelebrationProps {
  dealId: string;
  className?: string;
}

/**
 * CompletionCelebration - Displays a celebration UI when deal is completed
 */
export function CompletionCelebration({ dealId, className = "" }: CompletionCelebrationProps) {
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    // Hide confetti animation after 3 seconds
    const timer = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      className={`relative rounded-xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-green-50 p-8 text-center overflow-hidden ${className}`}
    >
      {/* Confetti animation overlay */}
      {showConfetti && (
        <div className="absolute inset-0 pointer-events-none">
          {/* eslint-disable react-hooks/purity -- decorative randomness is intentional */}
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 1}s`,
                animationDuration: `${2 + Math.random() * 2}s`,
              }}
            >
              <Sparkles
                className={`w-4 h-4 ${
                  i % 3 === 0
                    ? "text-emerald-400"
                    : i % 3 === 1
                      ? "text-green-400"
                      : "text-yellow-400"
                }`}
              />
            </div>
          ))}
          {/* eslint-enable react-hooks/purity */}
        </div>
      )}

      {/* Success icon */}
      <div className="relative mx-auto mb-6 w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center">
        <CheckCircle className="w-12 h-12 text-emerald-600" />
        <div className="absolute -top-1 -right-1">
          <PartyPopper className="w-6 h-6 text-yellow-500" />
        </div>
      </div>

      {/* Success message */}
      <h2 className="text-2xl font-bold text-emerald-800 mb-2">Herzlichen Glückwunsch!</h2>
      <p className="text-emerald-700 mb-6 max-w-md mx-auto">
        Ihre Transaktion wurde erfolgreich abgeschlossen. Vielen Dank, dass Sie den Ökopunktemarkt
        nutzen!
      </p>

      {/* Deal reference */}
      <div className="inline-block px-4 py-2 bg-emerald-100 rounded-full">
        <span className="text-sm font-medium text-emerald-700">
          Transaktion: {dealId.slice(0, 8).toUpperCase()}
        </span>
      </div>

      {/* Animation styles */}
      <style jsx>{`
        @keyframes confetti {
          0% {
            transform: translateY(-10px) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(400px) rotate(720deg);
            opacity: 0;
          }
        }
        .animate-confetti {
          animation: confetti linear forwards;
        }
      `}</style>
    </div>
  );
}
